import ApiService from '@/common/api.service'

import * as utils from '@/common/utils'
import * as security from '@/common/security'
import store from '@/store'

const groupNamespaces = {
  vehicle: 'assets/vehicleGroupsModule',
  unit: 'units/unitGroupsModule'
}

/**
 * Returns all properties and their default state.
 *
 * @return Object
 */
function properties () {
  return {
    id: null,
    name: '',
    partner: {
      id: null,
      name: ''
    },
    agency: {
      id: null,
      name: ''
    },
    client: {
      id: null,
      name: ''
    },
    created_at: '',
    updated_at: ''
  }
}

/**
 * Returns a copy of the default state.
 *
 * @return Object
 */
function defaults () {
  return {
    ...properties(),
    created_at: '',
    updated_at: ''
  }
}

function formDefaults () {
  return {
    partners: []
  }
}

/**
 * Returns a new object containing a transformed object
 * that can be consumed by the backend API.
 *
 * @param group Object
 *
 * @return Object
 */
function transformCreate (group) {
  return {
    id: group.id,
    name: group.name,
    partner_id: group.partner.id,
    agency_id: group.agency.id,
    client_id: group.client.id
  }
}

/**
 * Returns a new object containing a transformed object
 * that can be consumed by the backend API.
 *
 * @param group Object
 *
 * @return Object
 */
function transformUpdate (group) {
  return {
    id: group.id,
    name: group.name,
    partner_id: group.partner.id,
    agency_id: group.agency.id,
    client_id: group.client.id
  }
}

/**
 * The module's state (VueX store object)
 */
export const state = {
  data: Object.assign({}, defaults()),
  form: Object.assign({}, formDefaults()),
  original: properties(),
  groupType: '',
  isModified: false,
  isLoading: false
}

export const getters = {
  /**
   * Returns the full state object
   *
   * @return Object
   */
  get (state) {
    return state.data
  },

  /**
   * Returns the group type (either 'vehicle' or 'unit').
   *
   * @param state
   *
   * @returns {String}
   */
  getGroupType (state) {
    return state.groupType
  },

  /**
   * Returns the form's state object
   *
   * @return Object
   */
  getForm (state) {
    return state.form
  },

  /**
   * Returns true if the primary data have been modified
   * since it was originally loaded.
   *
   * @return Boolean
   */
  isModified (state) {
    return state.isModified
  },

  /**
   * Returns true if the data for the module is still being loaded.
   *
   * @return Boolean
   */
  isLoading (state) {
    return state.isLoading
  }

}

export const actions = {
  /**
   * Request a record from the API.
   *
   * @param context Object
   * @param id      Numeric
   *
   * @return Promise
   */
  fetch (context, id) {
    const groupType = context.getters['getGroupType']

    return ApiService
      .get(`${groupType}s/groups/${id}`)
      .then((response) => {
        context.commit('set', response.data.data)
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * Request "create" form data from the API.
   *
   * @param context Object
   *
   * @return Promise
   */
  fetchFormCreate (context) {
    const groupType = context.getters['getGroupType']

    return ApiService
      .get(`${groupType}s/groups/create`)
      .then((response) => {
        context.commit('set', defaults())
        context.commit('setFormData', response.data.data)
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * Request a record and "edit" form data from the API.
   *
   * @param context Object
   * @param id      Numeric
   *
   * @return Promise
   */
  fetchFormEdit (context, id) {
    const groupType = context.getters['getGroupType']

    return ApiService
      .get(`${groupType}s/groups/${id}/edit/`)
      .then((response) => {
        context.commit('set', response.data.data)
        context.commit('setFormData', response.data.data)
        context.commit('setOriginal', response.data.data)
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * POST (create) new record through the API and upon success, resets all.
   *
   * @param context Object
   *
   * @return Promise
   */
  post (context) {
    const groupType = context.getters['getGroupType']
    const groupNamespace = groupNamespaces[groupType]
    const group = context.getters['get']

    return ApiService
      .post(`${groupType}s/groups`, transformCreate(group))
      .then((response) => {
        context.commit('set', defaults())

        // Restore the partner, agency and client again because
        // we don't want to reset it after a post.
        context.commit('setPartnerIdName', group.partner)
        context.commit('setAgencyIdName', group.agency)
        context.commit('setClientIdName', group.client)

        context.commit('setIsModified', false)
        utils.addRecentHistory(groupNamespace, response.data.data)
        return response
      })
  },

  /**
   * PUT (update) existing record through the API and upon success, resets all.
   *
   * @param context Object
   *
   * @return Promise
   */
  put (context) {
    const groupType = context.getters['getGroupType']
    const group = context.getters['get']
    return ApiService
      .put(`${groupType}s/groups/${group.id}`, transformUpdate(group))
      .then((response) => {
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * DELETE existing record through the API.
   *
   * @param context Object
   *
   * @return Promise
   */
  delete (context) {
    const groupType = context.getters['getGroupType']
    const id = context.getters['get'].id
    return ApiService
      .delete(`${groupType}s/groups/${id}`)
      .then((response) => {
        return response
      })
  },

  /**
   * Queries (e.g. find) multiple records from the API.
   *
   * @param context Object
   * @param params  Array of query key's and values
   *
   * @return Promise
   */
  query (context, params) {
    const groupType = context.getters['getGroupType']
    return ApiService
      .get(`${groupType}s/groups`, params)
      .then((response) => {
        return response
      })
  }
}

export const mutations = {
  /**
   * Set all the data properties according to the provided data.
   *
   * @param state Object
   * @param data  Object
   */
  set (state, data) {
    state.data = Object.assign({}, data)
  },

  /**
   * Sets the group type, either 'unit' or 'vehicle'.
   *
   * @param state
   * @param groupType {String}
   */
  setGroupType (state, groupType) {
    state.groupType = groupType
  },

  setFormData (state, data) {
    state.form = {
      partners: data.partners
    }
  },

  /**
   * Sets the original primary data.
   *
   * @param state Object
   * @param data  Object
   */
  setOriginal (state, data) {
    state.original = {
      name: data.name
    }
  },

  /**
   * Set the group's partner, agency and client data.
   *
   * @param state
   * @param client
   */
  setClient (state, client) {
    const groupNamespace = groupNamespaces[state.groupType]
    store.commit(`${groupNamespace}/clearClient`)
    if (security.isAbovePartner()) {
      state.data.partner.id = client.partner.id
      state.data.partner.name = client.partner.name
    }
    if (security.isAboveAgency()) {
      state.data.agency.id = client.agency.id
      state.data.agency.name = client.agency.name
    }
    if (security.isAboveClient() || security.belongsToMultipleClients()) {
      state.data.client.id = client.id
      state.data.client.name = client.name
    }
  },

  /**
   * Sets the partner id and name
   * @param state
   * @param partner
   */
  setPartnerIdName (state, partner) {
    state.data.partner.id = partner.id
    state.data.partner.name = partner.name
  },

  /**
   * Sets the agency id and name
   * @param state
   * @param agency
   */
  setAgencyIdName (state, agency) {
    state.data.agency.id = agency.id
    state.data.agency.name = agency.name
  },

  /**
   * Sets the client id and name
   * @param state
   * @param client
   */
  setClientIdName (state, client) {
    state.data.client.id = client.id
    state.data.client.name = client.name
  },

  /**
   * Clear the group's partner agency and client data.
   *
   * @param state
   */
  clearClient (state) {
    state.data.partner.id = null
    state.data.partner.name = null
    state.data.agency.id = null
    state.data.agency.name = null
    state.data.client.id = null
    state.data.client.name = null
  },

  /**
   * Sets the "modified" status to true or false
   *
   * @param state Object
   * @param isModified Boolean
   */
  setIsModified (state, isModified) {
    state.isModified = isModified
  },

  /**
   * Sets the "isLoading" status to true or false
   *
   * @param state Object
   * @param isLoading Boolean
   */
  setIsLoading (state, isLoading) {
    state.isLoading = isLoading
  },

  /**
   * Sets a specific property and value
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value String
   */
  setProp (state, { name, value }) {
    state.isModified = true
    utils.mutateProp(state.data, name, value)
  },

  /**
   * Pushes a new element to an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value mixed  The variable to push onto the array.
   */
  pushProp (state, { name, value }) {
    state.isModified = true
    state.data[name].push(value)
  },

  /**
   * removes an element from an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param index Number The index of the element to remove
   */
  pullProp (state, { name, index }) {
    state.isModified = true
    utils.pullProp(state.data, name, index)
  },

  /**
   * Sets the values for multiple properties.
   *
   * @param state      Object
   * @param properties Array of objects in the form { name: 'propName', value: 'xyz' }
   */
  setProps (state, properties) {
    state.isModified = true
    properties.forEach(({ name, value }) => {
      utils.mutateProp(state.data, name, value)
    })
  },

  /**
   * Resets the state.
   *
   * @param state Object
   */
  reset (state) {
    state.data = defaults()
    state.groupType = ''
    state.original = properties()
    state.isModified = false
    state.isLoading = false
  },

  /**
   * Resets the form's state.
   *
   * @param state Object
   */
  resetForm (state) {
    state.form = formDefaults()
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/groups/groupsModule.js